"use client"

import * as React from "react"
import { useNavigate } from "react-router-dom"
import { ChevronsUpDown, Search } from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"

import type { Team } from "@/config"
import { useTeam } from '@/shared/context/TeamContext'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/shared/components/ui/sidebar'

export function TeamSwitcher({
  teams,
}: {
  teams: Team[]
}) {
  const { isMobile } = useSidebar()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { selectedTeam, setSelectedTeam } = useTeam()
  const [search, setSearch] = React.useState('')

  const visibleTeams = teams

  const activeTeam = visibleTeams.find(team => team.name === selectedTeam) || visibleTeams[0]

  React.useEffect(() => {
    if (visibleTeams.length > 0 && !visibleTeams.find(t => t.name === selectedTeam)) {
      setSelectedTeam(visibleTeams[0].name)
    }
  }, [selectedTeam, visibleTeams, setSelectedTeam])

  if (!activeTeam) {
    return null
  }

  const filteredTeams = search
    ? visibleTeams.filter(t => t.name.toLowerCase().includes(search.toLowerCase()))
    : visibleTeams

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu onOpenChange={(open) => { if (!open) setSearch('') }}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-black">
                <img
                  src={activeTeam.logo}
                  alt={activeTeam.name}
                  className="size-4 object-contain"
                />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{activeTeam.name}</span>
                <span className="truncate text-xs">{activeTeam.plan}</span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Equipos
            </DropdownMenuLabel>
            {visibleTeams.length > 5 && (
              <div className="px-2 pb-1.5">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="w-full rounded-md border bg-transparent py-1.5 pl-7 pr-2 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>
            )}
            <div className="max-h-[300px] overflow-y-auto">
              {filteredTeams.length > 0 ? (
                filteredTeams.map((team) => (
                  <DropdownMenuItem
                    key={team.name}
                    onClick={() => { setSelectedTeam(team.name); queryClient.invalidateQueries(); navigate('/') }}
                    className="gap-2 p-2"
                  >
                    <div className="flex size-6 items-center justify-center rounded-md border bg-black">
                      <img
                        src={team.logo}
                        alt={team.name}
                        className="size-3.5 object-contain"
                      />
                    </div>
                    {team.name}
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="px-2 py-4 text-center text-xs text-muted-foreground">
                  Sin resultados
                </div>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
